
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { ServiceFormFields } from "./form/ServiceFormFields";
import { ServiceFormActions } from "./form/ServiceFormActions";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ServiceFormValues } from "./form/types";

interface ServiceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service?: {
    id: string;
    service_name: string;
    description: string;
    duration: number;
    price: number;
    address?: string;
    phone?: string;
    email?: string;
    photo_url?: string;
    country?: string;
    category?: string;
    keywords?: string[];
    currency?: string;
  };
}

export const ServiceForm = ({ open, onOpenChange, service }: ServiceFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Pré-remplir le formulaire avec les données du service existant
  const form = useForm<ServiceFormValues>({
    defaultValues: {
      service_name: service?.service_name || "",
      description: service?.description || "",
      duration: service?.duration || 30,
      price: service?.price || 0,
      address: service?.address || "",
      phone: service?.phone || "",
      email: service?.email || "",
      country: service?.country || "",
      category: service?.category || "",
      keywords: service?.keywords?.join(", ") || "",
      currency: service?.currency || "EUR",
    },
  });

  const onSubmit = async (values: ServiceFormValues) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Vous devez être connecté pour ajouter un service",
        });
        return;
      }

      let photoUrl = service?.photo_url;

      if (values.photo) {
        const fileExt = values.photo.name.split('.').pop();
        const filePath = `${crypto.randomUUID()}.${fileExt}`;
        
        const { error: uploadError, data } = await supabase.storage
          .from('service-photos')
          .upload(filePath, values.photo);

        if (uploadError) throw uploadError;

        if (data) {
          const { data: { publicUrl } } = supabase.storage
            .from('service-photos')
            .getPublicUrl(filePath);
          photoUrl = publicUrl;
        }
      }

      const keywords = values.keywords
        ? values.keywords.split(',').map(k => k.trim())
        : [];

      const serviceData = {
        service_name: values.service_name,
        description: values.description,
        duration: values.duration,
        price: values.price,
        address: values.address,
        phone: values.phone,
        email: values.email,
        country: values.country,
        photo_url: photoUrl,
        category: values.category,
        keywords,
        currency: values.currency,
      };

      const { error } = service 
        ? await supabase
            .from("professional_services")
            .update(serviceData)
            .eq("id", service.id)
        : await supabase
            .from("professional_services")
            .insert({
              professional_id: user.id,
              ...serviceData,
            });

      if (error) throw error;

      toast({
        title: service ? "Service mis à jour" : "Service ajouté",
        description: service 
          ? "Le service a été mis à jour avec succès"
          : "Le service a été ajouté avec succès",
      });
      
      queryClient.invalidateQueries({ queryKey: ["professional-services"] });
      onOpenChange(false);
      form.reset();
    } catch (error) {
      console.error("Error in onSubmit:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: service
          ? "Impossible de mettre à jour le service"
          : "Impossible d'ajouter le service",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {service ? "Modifier le service" : "Ajouter un service"}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[calc(90vh-100px)] px-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <ServiceFormFields form={form} />
              <ServiceFormActions 
                isLoading={isLoading}
                onCancel={() => onOpenChange(false)}
                isEditing={!!service}
              />
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
